import { useEffect, useRef } from 'react';
import { WebhookEventCard } from './WebhookEventCard';
import { EmptyState } from '../conversations/EmptyState';
import { Loader2 } from 'lucide-react';
import { WebhookEventListItemDto, WebhookEventsQuery } from '../../types';
import { useWebhookEvents } from '../../hooks/useWebhookLogs';

interface WebhookEventListProps {
  filters?: WebhookEventsQuery;
  onEventSelect?: (eventId: string) => void;
  selectedEventId?: string;
}

export function WebhookEventList({ filters, onEventSelect, selectedEventId }: WebhookEventListProps) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useWebhookEvents(filters);

  const observerTarget = useRef<HTMLDivElement>(null);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten pages into single array
  const events = data?.pages.flatMap((page) => page.items) ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (events.length === 0) {
    return <EmptyState message="Nenhum webhook encontrado" />;
  }

  return (
    <div className="space-y-1">
      {events.map((event: WebhookEventListItemDto) => (
        <WebhookEventCard
          key={event.id}
          event={event}
          onClick={() => onEventSelect?.(event.id)}
          isSelected={event.id === selectedEventId}
        />
      ))}

      {/* Intersection observer target */}
      <div ref={observerTarget} className="h-4" />

      {/* Loading indicator */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* No more data indicator */}
      {!hasNextPage && events.length > 0 && (
        <div className="text-center text-sm text-muted-foreground py-4">
          Fim da lista
        </div>
      )}
    </div>
  );
}
